import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import request from '../../utils/fetcher'
import { useMe } from '../../hooks/useMe'
import { getMe } from '../../redux/actions'
import { buildContext } from './context'
import { visibleBoxes } from './boxes'
import ParcelBox, { FULL_ROW } from './Box'

// The boxes are independent, so one that throws costs its own card and nothing
// else. The boundary goes around the whole box rather than around a single
// part: a part's body is called as a plain function inside the box (that is how
// a box knows whether every part came back empty), so a part throws before
// there is any rendered output to wrap, and catching one part's throw mid-box
// would leave the hooks of the parts before it registered and shift every later
// part's hooks. A boundary that has caught nothing renders exactly what the box
// returned, null included. It tries again when the parcel is reloaded, since
// the data it fell over on may be gone.
class BoxBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidUpdate(prevProps) {
    const { resetKey } = this.props
    const { failed } = this.state
    if (failed && prevProps.resetKey !== resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ failed: false })
    }
  }

  componentDidCatch(error, errorInfo) {
    // the boundary swallows the error, so without this nothing outside the
    // user's own console would ever hear of a box failing
    const { id } = this.props
    Sentry.captureException(error, {
      tags: { parcelBox: id },
      contexts: { react: { componentStack: errorInfo?.componentStack } },
    })
  }

  render() {
    const { id, fullWidth, message, children } = this.props
    const { failed } = this.state
    if (!failed) return children
    return (
      <Card data-testid={`box-${id}`} sx={fullWidth ? FULL_ROW : undefined}>
        <CardContent>
          <Alert severity="warning" variant="outlined">
            {message}
          </Alert>
        </CardContent>
      </Card>
    )
  }
}

// The registry carries a titleKey, so the page is the one place that translates
// it and the box component stays free of i18n. The barcode box has no titleKey
// at all, hence the guard: t(undefined) is not a title.
function GuardedBox({
  id,
  titleKey,
  fullWidth,
  white,
  parts,
  parcel,
  onChanged,
}) {
  const { t } = useTranslation('parcels')
  return (
    <BoxBoundary
      id={id}
      fullWidth={fullWidth}
      resetKey={parcel}
      message={t('sectionFailed')}
    >
      <ParcelBox
        id={id}
        title={titleKey ? t(titleKey) : ''}
        fullWidth={fullWidth}
        white={white}
        parts={parts}
        parcel={parcel}
        onChanged={onChanged}
      />
    </BoxBoundary>
  )
}

export default function ParcelPage() {
  const { parcelId } = useParams()
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation('responseMessages')
  const { t: tParcels } = useTranslation('parcels')
  const { user, carrier } = useMe()
  const carrierRolePermissions = useSelector(
    (state) => state.root.carrierRolePermissions
  )
  const {
    user: signedInUser,
    carrier: signedInCarrier,
    carrierHeader,
  } = useSelector((state) => state.root)
  const [parcel, setParcel] = useState(null)
  // Told apart from "no such parcel": when the request itself threw we do not
  // know whether the parcel exists, and must not say that it does not.
  const [loadFailed, setLoadFailed] = useState(false)
  const [loading, setLoading] = useState(true)
  const shownParcel = useRef(null)
  const latestRequest = useRef(0)

  // The other modals are redux flags, and CarrierHome shows whichever is set. Opening a parcel
  // is a route, not a flag, so a flag left over from before — the location modal a section's
  // button asked for, then browser Back out of it — must not sit underneath this one.
  useEffect(() => {
    dispatch({ type: 'setShowModal', payload: {} })
  }, [dispatch])

  // CarrierHome loads the signed-in user, and on a pasted link it is doing so while this is
  // already rendering. Without the user's permissions the gated sections would appear a moment
  // later rather than being there, so this waits for them. The attempt is tracked because a
  // failed one is final: the reducer ignores it, and the view would spin for good.
  const meLoaded = Boolean(signedInUser && signedInCarrier)
  const [meAttempted, setMeAttempted] = useState(false)
  useEffect(() => {
    if (meLoaded) return undefined
    let active = true
    Promise.resolve(dispatch(getMe()))
      .catch(() => {})
      .then(() => {
        if (active) setMeAttempted(true)
      })
    return () => {
      active = false
    }
  }, [dispatch, meLoaded])

  const load = useCallback(async () => {
    latestRequest.current += 1
    const thisRequest = latestRequest.current
    let loaded = null
    let threw = false
    try {
      const response = await request(
        'GET',
        `/carrier/parcels/${parcelId}`,
        {},
        { headers: { 'x-iboxen-carrier': carrierHeader } }
      )
      loaded = response?.parcel ?? null
    } catch {
      // the backend answers 200 with no parcel for an id it does not know, so
      // a throw is always something going wrong (5xx, malformed id, no network)
      threw = true
    }
    // A newer load, or leaving the page, makes this answer stale.
    if (thisRequest !== latestRequest.current) return
    // A reload that fails (after a write, say) says nothing against the parcel
    // we already have; only a load that never had one reads as not found.
    const haveThisParcel = String(shownParcel.current?._id) === parcelId
    if (loaded || !haveThisParcel) {
      shownParcel.current = loaded
      setParcel(loaded)
      setLoadFailed(threw)
    }
    setLoading(false)
  }, [parcelId, carrierHeader])

  useEffect(() => {
    setLoading(true)
    load()
    return () => {
      latestRequest.current += 1
    }
  }, [load])

  let body
  if (!meLoaded && meAttempted) {
    body = <Alert severity="error">{t('Page-load-failed')}</Alert>
  } else if (loading || !meLoaded) {
    body = (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  } else if (!parcel && loadFailed) {
    body = <Alert severity="error">{t('Page-load-failed')}</Alert>
  } else if (!parcel) {
    body = <Alert severity="warning">{t('Parcel-not-found')}</Alert>
  } else {
    const context = buildContext({
      parcel,
      user,
      carrier,
      carrierRolePermissions,
    })
    const boxes = visibleBoxes(context)
    // a box keeps local state (an unticked box, an open editor), which belongs
    // to one parcel: another parcel gets boxes of its own, while a reload of the
    // same parcel keeps them
    const boxFor = (box) => (
      <GuardedBox
        key={`${box.id}:${parcel._id}`}
        id={box.id}
        titleKey={box.titleKey}
        fullWidth={box.fullWidth}
        white={box.white}
        parts={box.parts}
        parcel={parcel}
        onChanged={load}
      />
    )
    // Two columns of equal width, read left to right in registry order, and one
    // column on a narrow screen. A box the registry flags as full width spans
    // both columns where it stands — it is not lifted out of the grid, so such a
    // row can sit anywhere in the order and not only at the top. Which boxes
    // those are stays in the registry: moving a row is an edit to BOXES, not here.
    body = (
      <Box
        data-testid="parcel-grid"
        sx={{
          display: 'grid',
          // md, not sm: the breakpoint measures the window but the boxes live in a
          // dialog narrower than it, so a 600-900px window gave two columns of ~240px
          // and clipped the status and log tables inside them.
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          // no alignItems: the grid's default stretch makes a box take the height
          // of its tallest sibling, so the two columns stay level
          gap: 2,
        }}
      >
        {boxes.map(boxFor)}
      </Box>
    )
  }

  // Closing is navigating: the view has its own address, so leaving it is a route change and the
  // browser's own back button does the same thing as the cross.
  const close = () => history.push('/carrier')

  return (
    <Dialog open onClose={close} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6, py: 1.5 }}>
        {parcel?.parcelId ?? tParcels('parcel')}
        <IconButton
          aria-label={tParcels('close')}
          onClick={close}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2}>
          {body}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
